import React, { useEffect, useState, createContext, useContext } from 'react';
interface Badge {
  id: string;
  name: string;
  icon: string;
  description: string;
  earned: boolean;
}
interface UserPreferences {
  age: number | null;
  name: string;
  pronouns: string;
  gender: 'male' | 'female' | 'non-binary' | 'prefer-not-to-say' | null;
  language: 'en';
  theme: 'light' | 'dark';
  matureTopics: boolean;
  hasCompletedOnboarding: boolean;
}
interface ProgressData {
  points: number;
  streak: number;
  badges: Badge[];
  completedQuizzes: string[];
  completedStories: string[];
  completedModules: string[];
  currentModule: string | null;
  questionsAsked: number;
  preferences: UserPreferences;
  lastVisit: string;
}
interface ProgressContextType {
  progress: ProgressData;
  addPoints: (points: number) => void;
  incrementStreak: () => void;
  earnBadge: (badgeId: string) => void;
  completeQuiz: (quizId: string) => void;
  completeStory: (storyId: string) => void;
  completeModule: (moduleId: string) => void;
  incrementQuestions: () => void;
  updatePreferences: (prefs: Partial<UserPreferences>) => void;
  completeOnboarding: (prefs: UserPreferences) => void;
  canAccessModule: (moduleId: string) => boolean;
}
const ProgressContext = createContext<ProgressContextType | undefined>(undefined);
const initialBadges: Badge[] = [{
  id: 'first-question',
  name: 'Curious Mind',
  icon: '🤔',
  description: 'Asked your first question',
  earned: false
}, {
  id: 'quiz-master',
  name: 'Quiz Master',
  icon: '🎓',
  description: 'Completed 5 quizzes',
  earned: false
}, {
  id: 'story-explorer',
  name: 'Story Explorer',
  icon: '📖',
  description: 'Completed 3 stories',
  earned: false
}, {
  id: 'week-streak',
  name: 'Dedicated Learner',
  icon: '🔥',
  description: 'Maintained a 7-day streak',
  earned: false
}, {
  id: 'hundred-points',
  name: 'Century',
  icon: '💯',
  description: 'Earned 100 points',
  earned: false
}, {
  id: 'perfect-score',
  name: 'Perfectionist',
  icon: '⭐',
  description: 'Got 100% on a quiz',
  earned: false
}, {
  id: 'consent-champion',
  name: 'Consent Champion',
  icon: '🛡️',
  description: 'Completed all consent quizzes',
  earned: false
}, {
  id: 'five-hundred-points',
  name: 'Knowledge Seeker',
  icon: '🌟',
  description: 'Earned 500 points',
  earned: false
}, {
  id: 'all-stories',
  name: 'Story Master',
  icon: '📚',
  description: 'Completed all available stories',
  earned: false
}, {
  id: 'all-quizzes',
  name: 'Quiz Champion',
  icon: '🏆',
  description: 'Completed all available quizzes',
  earned: false
}];
const defaultPreferences: UserPreferences = {
  age: null,
  name: '',
  pronouns: 'they/them',
  gender: null,
  language: 'en',
  theme: 'light',
  matureTopics: false,
  hasCompletedOnboarding: false
};
export function ProgressProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [progress, setProgress] = useState<ProgressData>(() => {
    const saved = localStorage.getItem('safespace_progress');
    if (saved) {
      const parsed = JSON.parse(saved);
      const today = new Date().toDateString();
      const lastVisit = parsed.lastVisit ? new Date(parsed.lastVisit).toDateString() : null;
      if (lastVisit && lastVisit !== today) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const wasYesterday = lastVisit === yesterday.toDateString();
        return {
          ...parsed,
          streak: wasYesterday ? parsed.streak : 0,
          lastVisit: new Date().toISOString()
        };
      }
      return {
        ...parsed,
        lastVisit: new Date().toISOString()
      };
    }
    return {
      points: 0,
      streak: 0,
      badges: initialBadges,
      completedQuizzes: [],
      completedStories: [],
      completedModules: [],
      currentModule: null,
      questionsAsked: 0,
      preferences: defaultPreferences,
      lastVisit: new Date().toISOString()
    };
  });
  useEffect(() => {
    localStorage.setItem('safespace_progress', JSON.stringify(progress));
  }, [progress]);
  const canAccessModule = (moduleId: string): boolean => {
    // All modules are now accessible - no restrictions
    return true;
  };
  const addPoints = (points: number) => {
    setProgress(prev => {
      const newPoints = prev.points + points;
      const updatedBadges = [...prev.badges];
      if (newPoints >= 100 && !prev.badges.find(b => b.id === 'hundred-points')?.earned) {
        const badgeIndex = updatedBadges.findIndex(b => b.id === 'hundred-points');
        if (badgeIndex !== -1) updatedBadges[badgeIndex] = {
          ...updatedBadges[badgeIndex],
          earned: true
        };
      }
      return {
        ...prev,
        points: newPoints,
        badges: updatedBadges
      };
    });
  };
  const incrementStreak = () => {
    setProgress(prev => {
      const today = new Date().toDateString();
      const lastVisit = prev.lastVisit ? new Date(prev.lastVisit).toDateString() : null;
      if (lastVisit === today) return prev;
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const wasYesterday = lastVisit === yesterday.toDateString();
      const newStreak = wasYesterday ? prev.streak + 1 : 1;
      const updatedBadges = [...prev.badges];
      if (newStreak >= 7 && !prev.badges.find(b => b.id === 'week-streak')?.earned) {
        const badgeIndex = updatedBadges.findIndex(b => b.id === 'week-streak');
        if (badgeIndex !== -1) updatedBadges[badgeIndex] = {
          ...updatedBadges[badgeIndex],
          earned: true
        };
      }
      return {
        ...prev,
        streak: newStreak,
        badges: updatedBadges,
        lastVisit: new Date().toISOString()
      };
    });
  };
  const earnBadge = (badgeId: string) => {
    setProgress(prev => ({
      ...prev,
      badges: prev.badges.map(b => b.id === badgeId ? {
        ...b,
        earned: true
      } : b)
    }));
  };
  const completeModule = (moduleId: string) => {
    setProgress(prev => {
      if (prev.completedModules.includes(moduleId)) return prev;
      return {
        ...prev,
        completedModules: [...prev.completedModules, moduleId]
      };
    });
  };
  const completeQuiz = (quizId: string) => {
    setProgress(prev => {
      if (prev.completedQuizzes.includes(quizId)) return prev;
      const newCompleted = [...prev.completedQuizzes, quizId];
      const updatedBadges = [...prev.badges];
      if (newCompleted.length >= 5 && !prev.badges.find(b => b.id === 'quiz-master')?.earned) {
        const badgeIndex = updatedBadges.findIndex(b => b.id === 'quiz-master');
        if (badgeIndex !== -1) updatedBadges[badgeIndex] = {
          ...updatedBadges[badgeIndex],
          earned: true
        };
      }
      return {
        ...prev,
        completedQuizzes: newCompleted,
        badges: updatedBadges
      };
    });
  };
  const completeStory = (storyId: string) => {
    setProgress(prev => {
      if (prev.completedStories.includes(storyId)) return prev;
      const newCompleted = [...prev.completedStories, storyId];
      const updatedBadges = [...prev.badges];
      if (newCompleted.length >= 3 && !prev.badges.find(b => b.id === 'story-explorer')?.earned) {
        const badgeIndex = updatedBadges.findIndex(b => b.id === 'story-explorer');
        if (badgeIndex !== -1) updatedBadges[badgeIndex] = {
          ...updatedBadges[badgeIndex],
          earned: true
        };
      }
      return {
        ...prev,
        completedStories: newCompleted,
        badges: updatedBadges
      };
    });
  };
  const incrementQuestions = () => {
    setProgress(prev => {
      const newCount = prev.questionsAsked + 1;
      const updatedBadges = [...prev.badges];
      if (newCount === 1 && !prev.badges.find(b => b.id === 'first-question')?.earned) {
        const badgeIndex = updatedBadges.findIndex(b => b.id === 'first-question');
        if (badgeIndex !== -1) updatedBadges[badgeIndex] = {
          ...updatedBadges[badgeIndex],
          earned: true
        };
      }
      return {
        ...prev,
        questionsAsked: newCount,
        badges: updatedBadges
      };
    });
  };
  const updatePreferences = (prefs: Partial<UserPreferences>) => {
    setProgress(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        ...prefs
      }
    }));
  };
  const completeOnboarding = (prefs: UserPreferences) => {
    setProgress(prev => ({
      ...prev,
      preferences: {
        ...prefs,
        hasCompletedOnboarding: true
      },
      streak: 1
    }));
  };
  return <ProgressContext.Provider value={{
    progress,
    addPoints,
    incrementStreak,
    earnBadge,
    completeQuiz,
    completeStory,
    completeModule,
    incrementQuestions,
    updatePreferences,
    completeOnboarding,
    canAccessModule
  }} data-id="element-287">
      {children}
    </ProgressContext.Provider>;
}
export function useProgress() {
  const context = useContext(ProgressContext);
  if (!context) throw new Error('useProgress must be used within ProgressProvider');
  return context;
}